import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import { FilePlus2, FolderKanban, MapPinned, Eye, Mountain } from 'lucide-react';
import { projectZone } from './MapView';
import { pipeLatLngs } from '../lib/model';

const PREF_KEY = 'giss_start_pref';
export const getStartPref = () => (['project', 'single', 'free'].includes(localStorage.getItem(PREF_KEY)) ? localStorage.getItem(PREF_KEY) : 'ask');
export const setStartPref = (v) => localStorage.setItem(PREF_KEY, v);
// "on opening always start with …" – used in the chooser and in the settings
export function StartPref({ t, testid = 'start-pref-select' }) {
  const [v, setV] = useState(getStartPref);
  return (
    <label className="stc-pref">{t.stcPref}
      <select data-testid={testid} value={v} onChange={(e) => { setV(e.target.value); setStartPref(e.target.value); }}>
        {['ask', 'project', 'single', 'free'].map((k) => <option key={k} value={k}>{t['stcPref_' + k]}</option>)}
      </select>
      <small>{t.stcPrefHint}</small>
    </label>
  );
}
export const ZONE_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0891b2', '#ca8a04', '#db2777'];
const ptsOf = (p) => { const w = [...p.reports.map((r) => [r.lat, r.lon]), ...(p.pipes || []).flatMap((x) => pipeLatLngs(x, p.fittings || [])), ...(p.fittings || []).map((f) => [f.lat, f.lon])].filter((x) => x && x[0] != null); return w.length ? w : [[p.lat, p.lon]].filter((x) => x[0] != null); };

// overview of all works: project zones (own colour) + single reports; tap opens the work
function AllMap({ projects, onOpen }) {
  const el = useRef(null);
  useEffect(() => {
    const map = L.map(el.current, { zoomControl: true, attributionControl: false }).setView([47, 11], 5);
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { maxZoom: 19 }).addTo(map);
    const all = [];
    projects.forEach((p, i) => {
      const pts = ptsOf(p), c = p.color || ZONE_COLORS[i % ZONE_COLORS.length];
      if (!pts.length) return;
      all.push(...pts);
      const zone = p.mode !== 'single' && projectZone(pts);
      if (zone) L.polygon(zone, { color: c, weight: 3, dashArray: '10 6', fillColor: c, fillOpacity: 0.12 }).on('click', () => onOpen(p.id)).bindTooltip(p.title || '—', { sticky: true }).addTo(map);
      L.marker(pts[0], { icon: L.divIcon({ className: 'pin-wrap', html: `<span class="stc-mk" style="background:${c}" data-testid="start-map-mk-${i}">${p.mode === 'single' ? 'R' : 'P'}</span>`, iconSize: [28, 28], iconAnchor: [14, 14] }) }).on('click', () => onOpen(p.id)).bindTooltip(p.title || '—').addTo(map);
    });
    if (all.length) map.fitBounds(L.latLngBounds(all), { padding: [30, 30], maxZoom: 18 });
    return () => map.remove();
  }, [projects, onOpen]);
  return <div ref={el} className="stc-map" data-testid="start-all-map" />;
}

export default function StartChooser({ t, projects, onCreate, onOpen, onClose, onBrowse, onFree }) {
  const [mode, setMode] = useState(null);
  const [name, setName] = useState('');
  const works = projects.filter((p) => !(p.__fresh && p.updatedAt === p.createdAt));
  return createPortal(
    <div className="stc-overlay" data-testid="start-chooser">
      <div className="stc-box">
        <h2>{t.stcTitle}</h2>
        <div className="stc-choice">
          <button type="button" className={`stc-card ${mode === 'single' ? 'on' : ''}`} data-testid="start-single-btn" onClick={() => setMode('single')}><FilePlus2 size={28} /><b>{t.stcSingle}</b><small>{t.stcSingleD}</small></button>
          <button type="button" className={`stc-card proj ${mode === 'project' ? 'on' : ''}`} data-testid="start-project-btn" onClick={() => setMode('project')}><FolderKanban size={28} /><b>{t.stcProject}</b><small>{t.stcProjectD}</small></button>
          <button type="button" className="stc-card look" data-testid="start-browse-btn" onClick={onBrowse}><Eye size={28} /><b>{t.stcBrowse}</b><small>{t.stcBrowseD}</small></button>
          <button type="button" className="stc-card free" data-testid="start-free-btn" onClick={onFree}><Mountain size={28} /><b>{t.stcFree}</b><small>{t.stcFreeD}</small></button>
        </div>
        {mode && (
          <form className="stc-name" onSubmit={(e) => { e.preventDefault(); onCreate(mode, name.trim()); }}>
            <input autoFocus data-testid="start-name-input" placeholder={mode === 'single' ? t.stcNameRep : t.stcNameProj} value={name} onChange={(e) => setName(e.target.value)} />
            <button className="btn btn-accent" type="submit" data-testid="start-create-btn">{t.stcCreate}</button>
          </form>
        )}
        <h4><MapPinned size={16} /> {t.stcAll} ({works.length})</h4>
        {works.length ? <AllMap projects={works} onOpen={onOpen} /> : <p className="muted" data-testid="start-empty">{t.stcEmpty}</p>}
        <div className="stc-list">
          {works.map((p, i) => <button key={p.id} type="button" data-testid={`start-work-${i}`} onClick={() => onOpen(p.id)}><i style={{ background: p.color || ZONE_COLORS[projects.indexOf(p) % ZONE_COLORS.length] }} />{p.mode === 'single' ? t.modeSingle : t.modeProject}: <b>{p.title || '—'}</b>{p.mode !== 'single' && <em>{p.reports.length}</em>}</button>)}
        </div>
        <StartPref t={t} />
        {works.length > 0 && <button type="button" className="btn btn-outline btn-sm" data-testid="start-close-btn" onClick={onClose}>{t.stcContinue}</button>}
      </div>
    </div>,
    document.body,
  );
}
